import { forwardRef, useId, type ChangeEvent } from "react";
import { cn } from "@/shared/lib/cn";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  className?: string;
  id?: string;
}

function formatPhoneMask(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  // Always starts with 7
  const d = digits.startsWith("7") ? digits.slice(1) : digits.startsWith("8") ? digits.slice(1) : digits;

  let result = "+7";
  if (d.length > 0) result += ` (${d.slice(0, 3)}`;
  if (d.length >= 3) result += `)`;
  if (d.length > 3) result += ` ${d.slice(3, 6)}`;
  if (d.length > 6) result += `-${d.slice(6, 8)}`;
  if (d.length > 8) result += `-${d.slice(8, 10)}`;
  return result;
}

function extractDigits(formatted: string): string {
  return formatted.replace(/\D/g, "");
}

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, label, error, className, id }, ref) => {
    const autoId = useId();
    const inputId = id || autoId;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const digits = extractDigits(raw);
      // Limit to 11 digits (7 + 10)
      const limited = digits.slice(0, 11);
      onChange(formatPhoneMask(limited));
    };

    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-sand-700 dark:text-sand-300">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type="tel"
          inputMode="numeric"
          placeholder="+7 (___) ___-__-__"
          value={value}
          onChange={handleChange}
          className={cn(
            "w-full h-10 px-3 rounded-xl",
            "bg-white border border-sand-300 text-sand-900",
            "placeholder:text-sand-400 text-sm font-mono",
            "transition-colors duration-200",
            "hover:border-ocean-300",
            "focus:outline-none focus:border-ocean-500 focus:ring-2 focus:ring-ocean-500/20",
            "dark:bg-sand-800 dark:border-sand-700 dark:text-sand-100 dark:placeholder:text-sand-500",
            error && "border-coral-500 focus:border-coral-500 focus:ring-coral-500/20",
            className,
          )}
        />
        {error && <p className="text-xs text-coral-500 pl-0.5">{error}</p>}
      </div>
    );
  },
);

PhoneInput.displayName = "PhoneInput";
